import { Artboard } from '@/components/builder/artboard';
import { Inspector } from '@/components/builder/inspector';
import { LayersTree } from '@/components/builder/layers-tree';
import { insertionPoint, Palette } from '@/components/builder/palette';
import { PreflightPanel } from '@/components/builder/preflight-panel';
import { PreviewDialog } from '@/components/builder/preview-dialog';
import { StepsStrip } from '@/components/builder/steps-strip';
import { useAutosave, type SaveState } from '@/components/builder/use-autosave';
import { ViewportToolbar } from '@/components/builder/viewport-toolbar';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useExperience } from '@/lib/queries';
import { useContextStore } from '@/stores/context';
import type { Action } from '@personalizehq/schemas/ir';
import { toast } from 'sonner';
import {
  addStep,
  createSession,
  findStep,
  insertNode,
  moveStep,
  newBlock,
  removeNode,
  removeStep,
  setLabel,
  stepToExperienceDocument,
  type AuthoringDoc,
  type BlockKind,
  type SessionStore,
} from '@personalizehq/editor';
import type { Viewport } from '@personalizehq/schemas/ir';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ChevronLeft, Frame } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useStore } from 'zustand';

export const Route = createFileRoute('/_canvas/experiences/$id/edit')({ component: Builder });

function Builder(): React.JSX.Element {
  const { id } = Route.useParams();
  const { data: experience, isLoading } = useExperience(id);

  if (isLoading || !experience) {
    return <div className="grid h-screen place-items-center bg-zinc-900 text-sm text-zinc-400">Loading…</div>;
  }

  // `key` remounts the session when the experience changes: the store is seeded from the doc once
  // and owns it from then on (which is why the query is `staleTime: Infinity` — a refetch swapping
  // the doc out from under an open canvas would silently discard edits).
  return (
    <BuilderShell
      key={experience.id}
      experienceId={experience.id}
      name={experience.name}
      document={experience.document}
    />
  );
}

const DEFAULT_VIEWPORT: Viewport = { w: 390, h: 844, color_scheme: 'light', font_scale: 1, direction: 'ltr' };

function BuilderShell({
  experienceId,
  name,
  document: initialDoc,
}: {
  experienceId: string;
  name: string;
  document: AuthoringDoc;
}): React.JSX.Element {
  const appId = useContextStore((s) => s.appId);
  const [session] = useState<SessionStore>(() => createSession(initialDoc));

  const doc = useStore(session, (s) => s.doc);
  const selection = useStore(session, (s) => s.selection);
  const focusedStepId = useStore(session, (s) => s.focusedStepId);

  const [viewport, setViewport] = useState<Viewport>(DEFAULT_VIEWPORT);
  const [outlines, setOutlines] = useState(false);
  const saveState = useAutosave(experienceId, appId, doc);

  const step = findStep(doc, focusedStepId);
  const experienceDoc = useMemo(() => stepToExperienceDocument(doc, focusedStepId), [doc, focusedStepId]);

  // INTERACTIVE PREVIEW (B4). Until now every button on the canvas decoded its actions and fired
  // nothing — the renderer has always taken an `onAction` and the artboard simply never passed one.
  //
  // `flow_next`/`flow_back` walk the steps, which is the only way to TRY a flow rather than inspect
  // its steps one at a time. The rest are reported rather than performed: opening a customer's deep
  // link from the dashboard, or firing a real analytics event from a draft, would be the editor
  // doing something the author did not ask for.
  const onAction = useCallback(
    (action: Action): void => {
      const { doc: current, focusedStepId: at, focusStep } = session.getState();
      const index = current.steps.findIndex((s) => s.id === at);

      if (action.type === 'flow_next' || action.type === 'flow_back') {
        const next = current.steps[index + (action.type === 'flow_next' ? 1 : -1)];
        if (next) focusStep(next.id);
        else toast.info(action.type === 'flow_next' ? 'Last step — this dismisses on device.' : 'First step.');
        return;
      }

      toast.info(
        action.type === 'dismiss'
          ? 'Dismiss — the experience closes on device.'
          : `${action.type}${'url' in action ? `: ${action.url}` : 'name' in action ? `: ${action.name}` : ''}`
      );
    },
    [session]
  );

  const insert = useCallback(
    (kind: BlockKind) => {
      const { applyChange, select, doc: current, focusedStepId: stepId, selection: selected } = session.getState();
      const root = findStep(current, stepId)!.root;
      const { parentId, index } = insertionPoint(root, selected);
      const node = newBlock(kind, current.theme);
      applyChange((draft) => insertNode(findStep(draft, stepId)!.root, parentId, index, node));
      // `applyChange` does not select what it inserted — an insert you cannot immediately edit is
      // a click wasted.
      select(node.id);
    },
    [session]
  );

  const remove = useCallback(() => {
    const { applyChange, doc: current, focusedStepId: stepId, selection: selected } = session.getState();
    const root = findStep(current, stepId)!.root;
    if (selected === null || selected === root.id) return; // the root is not removable
    applyChange((draft) => removeNode(findStep(draft, stepId)!.root, selected));
  }, [session]);

  // ⌘Z / ⇧⌘Z / Delete / ⌘S. Never while the author is typing into a field — Delete in an inspector
  // input must delete a character, not the block being edited.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable]')) return;
      const meta = e.metaKey || e.ctrlKey;

      if (meta && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) session.getState().redo();
        else session.getState().undo();
      } else if (meta && e.key.toLowerCase() === 's') {
        e.preventDefault(); // autosave already owns this; suppress the browser's Save Page dialog
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        remove();
      } else if (e.key === 'Escape') {
        session.getState().select(null);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [session, remove]);

  if (!step) return <div className="grid h-screen place-items-center">This document has no steps.</div>;

  return (
    <div className="flex h-screen flex-col bg-background text-[13px]">
      <header className="flex h-12 shrink-0 items-center gap-3 border-b px-3">
        <Button variant="ghost" size="sm" asChild className="text-muted-foreground">
          <Link to="/experiences">
            <ChevronLeft className="size-4" />
            Experiences
          </Link>
        </Button>
        <span className="font-medium">{name}</span>
        <SaveBadge state={saveState} />

        <Separator orientation="vertical" className="mx-1 h-5" />

        <StepsStrip
          steps={doc.steps}
          focusedStepId={focusedStepId}
          onFocus={(id) => session.getState().focusStep(id)}
          onAdd={() =>
            session.getState().applyChange((draft) => {
              addStep(draft);
            })
          }
          onRemove={(id) => session.getState().applyChange((draft) => removeStep(draft, id))}
          onMove={(id, index) => session.getState().applyChange((draft) => moveStep(draft, id, index))}
        />

        <div className="ml-auto flex items-center gap-3">
          {/* Plasmic's View menu in one button. Wireframing every rect costs a loop — we already
              have every rect from the solver. */}
          <Button
            variant="ghost"
            size="sm"
            aria-pressed={outlines}
            className={outlines ? 'text-foreground' : 'text-muted-foreground'}
            onClick={() => setOutlines(!outlines)}
          >
            <Frame className="size-4" />
            Outlines
          </Button>
          <PreflightPanel
            doc={doc}
            onGo={(stepId, nodeId) => {
              const { focusStep, select } = session.getState();
              focusStep(stepId);
              // `focusStep` clears the selection, so the select must follow it, not race it.
              if (nodeId) select(nodeId);
            }}
          />
          <PreviewDialog experienceId={experienceId} />
          <ViewportToolbar viewport={viewport} onChange={setViewport} />
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <aside className="flex w-[260px] shrink-0 flex-col border-r">
          <PanelTitle>Layers</PanelTitle>
          <div className="min-h-0 flex-1 overflow-y-auto px-1">
            <LayersTree
              root={step.root}
              labels={doc.labels}
              selection={selection}
              onSelect={(id) => session.getState().select(id)}
              onRename={(id, name) =>
                session.getState().applyChange((draft) => setLabel(draft, id, name), `${id}.label`)
              }
            />
          </div>
          <div className="border-t">
            <PanelTitle>Insert</PanelTitle>
            <Palette onInsert={insert} />
          </div>
        </aside>

        {/* The surround is dark in BOTH themes — the artboard pops like a phone in a dim room. */}
        <main className="grid flex-1 place-items-center overflow-auto bg-zinc-900 p-8">
          <Artboard
            doc={experienceDoc}
            viewport={viewport}
            selection={selection}
            outlines={outlines}
            onSelect={(id) => session.getState().select(id)}
            onAction={onAction}
          />
        </main>

        <aside className="w-[300px] shrink-0 overflow-y-auto border-l">
          <Inspector session={session} />
        </aside>
      </div>
    </div>
  );
}

function PanelTitle({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </div>
  );
}

const SAVE_LABEL: Record<SaveState, string> = {
  saved: 'Saved',
  dirty: 'Unsaved',
  saving: 'Saving…',
  error: 'Save failed',
};

function SaveBadge({ state }: { state: SaveState }): React.JSX.Element {
  return (
    <span className={state === 'error' ? 'text-[12px] text-destructive' : 'text-[12px] text-muted-foreground'}>
      {SAVE_LABEL[state]}
    </span>
  );
}
